import React, { useState, useEffect } from 'react';
import { notificationAPI, customerAPI } from '../api/axios';
import Button from '../components/Button';
import {
  Bell,
  Send,
  RefreshCw,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  Search,
  AlertTriangle,
  Settings,
  Zap,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import './NotificationPage.css';

/**
 * 알림 관리 페이지
 * - 발송 히스토리 조회
 * - 커스텀 메시지 수동 발송
 * - D-1 리마인드 수동 트리거
 * - 알림 통계
 */
const NotificationPage = () => {
  // 상태 관리
  const [notifications, setNotifications] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('history'); // history | send | settings

  // 수동 발송 상태
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customMessage, setCustomMessage] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const [sendResult, setSendResult] = useState(null);

  // 페이지네이션
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 15;

  // 데이터 로드
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [notiRes, statsRes] = await Promise.all([
        notificationAPI.getAll(),
        notificationAPI.getStats()
      ]);
      setNotifications(notiRes.data);
      setStats(statsRes.data);
    } catch (err) {
      console.error('알림 데이터 로드 실패:', err);
    } finally {
      setLoading(false);
    }
  };

  // 고객 검색
  const handleSearch = async () => {
    if (!searchTerm.trim()) return;
    setIsSearching(true);
    try {
      const res = await customerAPI.search(searchTerm);
      setSearchResults(res.data);
    } catch (err) {
      console.error('검색 실패:', err);
    } finally {
      setIsSearching(false);
    }
  };

  // 고객 선택
  const handleSelectCustomer = (customer) => {
    setSelectedCustomer(customer);
    setSearchResults([]);
    setSearchTerm('');
  };

  // 커스텀 메시지 발송
  const handleSendMessage = async () => {
    if (!selectedCustomer || !customMessage.trim()) {
      alert('고객을 선택하고 메시지를 입력하세요.');
      return;
    }

    setIsSending(true);
    setSendResult(null);

    try {
      const res = await notificationAPI.send({
        customerId: selectedCustomer.id,
        message: customMessage
      });

      if (res.data.status === 'SUCCESS') {
        setSendResult({ type: 'success', message: '메시지가 발송되었습니다.' });
        setCustomMessage('');
        setSelectedCustomer(null);
        fetchData(); // 히스토리 새로고침
      } else {
        setSendResult({ type: 'error', message: '발송에 실패했습니다: ' + (res.data.errorMessage || '') });
      }
    } catch (err) {
      setSendResult({ type: 'error', message: '발송 중 오류가 발생했습니다.' });
    } finally {
      setIsSending(false);
    }
  };

  // D-1 리마인드 수동 트리거
  const handleTriggerReminders = async () => {
    if (!window.confirm('내일 예약 고객들에게 리마인드 알림을 발송하시겠습니까?')) return;

    try {
      const res = await notificationAPI.triggerReminders();
      alert(res.data.message);
      fetchData();
    } catch (err) {
      alert('리마인드 발송에 실패했습니다.');
    }
  };

  // 알림 유형 한글 변환
  const getTypeLabel = (type) => {
    const labels = {
      RESERVATION_CREATED: '예약 접수',
      RESERVATION_CONFIRMED: '예약 확정',
      RESERVATION_REMINDER: 'D-1 리마인드',
      TREATMENT_COMPLETED: '시술 완료',
      RESERVATION_CANCELLED: '예약 취소',
      CUSTOM: '수동 발송'
    };
    return labels[type] || type;
  };

  // 상태 아이콘
  const getStatusIcon = (status) => {
    switch (status) {
      case 'SUCCESS': return <CheckCircle size={14} className="status-icon success" />;
      case 'FAILED': return <XCircle size={14} className="status-icon failed" />;
      case 'SKIPPED': return <AlertTriangle size={14} className="status-icon skipped" />;
      default: return <Clock size={14} className="status-icon pending" />;
    }
  };

  // 날짜 포맷
  const formatDateTime = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${month}/${day} ${hours}:${minutes}`;
  };

  // 유형별 색상
  const getTypeBadgeClass = (type) => {
    switch (type) {
      case 'RESERVATION_CREATED': return 'type-created';
      case 'RESERVATION_CONFIRMED': return 'type-confirmed';
      case 'RESERVATION_REMINDER': return 'type-reminder';
      case 'TREATMENT_COMPLETED': return 'type-completed';
      case 'RESERVATION_CANCELLED': return 'type-cancelled';
      case 'CUSTOM': return 'type-custom';
      default: return '';
    }
  };

  // 페이지네이션
  const totalPages = Math.ceil(notifications.length / itemsPerPage);
  const currentItems = notifications.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  return (
    <div className="notification-page">
      {/* 상단 통계 카드 */}
      {stats && (
        <div className="noti-stats-grid">
          <div className="noti-stat-card">
            <div className="noti-stat-icon channel">
              <Bell size={22} />
            </div>
            <div className="noti-stat-info">
              <span className="noti-stat-label">발송 채널</span>
              <span className="noti-stat-value">{stats.channel}</span>
            </div>
          </div>
          <div className="noti-stat-card">
            <div className="noti-stat-icon success">
              <CheckCircle size={22} />
            </div>
            <div className="noti-stat-info">
              <span className="noti-stat-label">오늘 발송</span>
              <span className="noti-stat-value">{stats.todaySuccess}건</span>
            </div>
          </div>
          <div className="noti-stat-card">
            <div className="noti-stat-icon failed">
              <XCircle size={22} />
            </div>
            <div className="noti-stat-info">
              <span className="noti-stat-label">오늘 실패</span>
              <span className="noti-stat-value">{stats.todayFailed}건</span>
            </div>
          </div>
          <div className="noti-stat-card">
            <div className="noti-stat-icon total">
              <MessageSquare size={22} />
            </div>
            <div className="noti-stat-info">
              <span className="noti-stat-label">누적 발송</span>
              <span className="noti-stat-value">{stats.totalSent}건</span>
            </div>
          </div>
        </div>
      )}

      {/* 탭 네비게이션 */}
      <div className="noti-tabs">
        <button
          className={`noti-tab ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          <Clock size={16} />
          발송 히스토리
        </button>
        <button
          className={`noti-tab ${activeTab === 'send' ? 'active' : ''}`}
          onClick={() => setActiveTab('send')}
        >
          <Send size={16} />
          메시지 발송
        </button>
        <button
          className={`noti-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          <Settings size={16} />
          설정 안내
        </button>
      </div>

      {/* ─── 탭: 발송 히스토리 ─── */}
      {activeTab === 'history' && (
        <div className="noti-section">
          <div className="section-header-row">
            <h3>📋 발송 히스토리</h3>
            <div className="header-actions">
              <Button variant="primary" size="small" onClick={handleTriggerReminders}>
                <Zap size={14} />
                <span style={{marginLeft: '4px'}}>D-1 리마인드 발송</span>
              </Button>
              <Button variant="outline" size="small" onClick={fetchData}>
                <RefreshCw size={14} />
                <span style={{marginLeft: '4px'}}>새로고침</span>
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="noti-loading">불러오는 중...</div>
          ) : notifications.length === 0 ? (
            <div className="noti-empty">
              <Bell size={48} className="empty-icon" />
              <p>발송된 알림이 없습니다.</p>
              <p className="empty-hint">예약이 생성되면 자동으로 알림이 발송됩니다.</p>
            </div>
          ) : (
            <>
              <div className="noti-table-wrapper">
                <table className="noti-table">
                  <thead>
                    <tr>
                      <th>상태</th>
                      <th>유형</th>
                      <th>수신자</th>
                      <th>내용</th>
                      <th>채널</th>
                      <th>발송시각</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((noti) => (
                      <tr key={noti.id} className={`status-row-${noti.status.toLowerCase()}`}>
                        <td className="status-cell">
                          {getStatusIcon(noti.status)}
                          <span className={`status-text ${noti.status.toLowerCase()}`}>
                            {noti.status}
                          </span>
                        </td>
                        <td>
                          <span className={`type-badge ${getTypeBadgeClass(noti.type)}`}>
                            {getTypeLabel(noti.type)}
                          </span>
                        </td>
                        <td className="recipient-cell">{noti.recipientPhone}</td>
                        <td className="content-cell">
                          <div className="content-preview" title={noti.content}>
                            {noti.content.length > 50 
                              ? noti.content.substring(0, 50) + '...'
                              : noti.content
                            }
                          </div>
                          {noti.errorMessage && (
                            <div className="error-text">⚠ {noti.errorMessage}</div>
                          )}
                        </td>
                        <td>
                          <span className="channel-badge">{noti.channel}</span>
                        </td>
                        <td className="time-cell">{formatDateTime(noti.sentAt)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* 페이지네이션 */}
              {totalPages > 1 && (
                <div className="noti-pagination">
                  <Button
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    variant="outline"
                    size="small"
                  >
                    <ChevronLeft size={16} />
                  </Button>
                  <span className="page-info">{currentPage} / {totalPages}</span>
                  <Button
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    variant="outline"
                    size="small"
                  >
                    <ChevronRight size={16} />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* ─── 탭: 메시지 발송 ─── */}
      {activeTab === 'send' && (
        <div className="noti-section">
          <h3>✉️ 커스텀 메시지 발송</h3>
          <p className="section-desc">특정 고객에게 직접 메시지를 발송합니다.</p>

          {/* 고객 검색 */}
          <div className="send-form">
            <div className="form-group">
              <label><Search size={14} /> 고객 검색</label>
              <div className="search-row">
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                  placeholder="이름 또는 전화번호"
                  disabled={isSearching}
                />
                <Button onClick={handleSearch} disabled={isSearching} variant="primary" size="medium" loading={isSearching}>
                  검색
                </Button>
              </div>

              {/* 검색 결과 */}
              {searchResults.length > 0 && (
                <div className="search-dropdown">
                  {searchResults.map(c => (
                    <div
                      key={c.id}
                      className="search-item"
                      onClick={() => handleSelectCustomer(c)}
                    >
                      <strong>{c.name}</strong>
                      <span>{c.phone}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* 선택된 고객 */}
            {selectedCustomer && (
              <div className="selected-customer">
                <span>📱 수신자: <strong>{selectedCustomer.name}</strong> ({selectedCustomer.phone})</span>
                <Button onClick={() => setSelectedCustomer(null)} variant="outline" size="small">✕</Button>
              </div>
            )}

            {/* 메시지 입력 */}
            <div className="form-group">
              <label><MessageSquare size={14} /> 메시지 내용</label>
              <textarea
                value={customMessage}
                onChange={(e) => setCustomMessage(e.target.value)}
                placeholder="발송할 메시지를 입력하세요..."
                rows={6}
                maxLength={1000}
              />
              <div className="char-count">{customMessage.length}/1000</div>
            </div>

            {/* 발송 결과 */}
            {sendResult && (
              <div className={`send-result ${sendResult.type}`}>
                {sendResult.type === 'success' ? <CheckCircle size={16} /> : <XCircle size={16} />}
                {sendResult.message}
              </div>
            )}

            {/* 발송 버튼 */}
            <Button
              variant="primary"
              size="large"
              fullWidth
              onClick={handleSendMessage}
              disabled={!selectedCustomer || !customMessage.trim() || isSending}
              loading={isSending}
            >
              <Send size={16} />
              <span style={{marginLeft: '8px'}}>메시지 발송</span>
            </Button>
          </div>
        </div>
      )}

      {/* ─── 탭: 설정 안내 ─── */}
      {activeTab === 'settings' && (
        <div className="noti-section">
          <h3>⚙️ 알림 시스템 설정 안내</h3>

          <div className="settings-card">
            <div className="settings-status">
              <span className="settings-label">현재 상태</span>
              <span className={`settings-badge ${stats?.enabled ? 'enabled' : 'disabled'}`}>
                {stats?.enabled ? '✅ 활성화' : '⏸️ 비활성화'}
              </span>
            </div>
            <div className="settings-status">
              <span className="settings-label">발송 채널</span>
              <span className="settings-badge channel">
                {stats?.channel === 'LOG' ? '🖥️ 개발 모드 (콘솔 출력)' : '📱 실서비스 (Solapi)'}
              </span>
            </div>
          </div>

          <div className="settings-guide">
            <h4>🚀 실서비스 전환 방법 (카카오 알림톡)</h4>
            <div className="guide-steps">
              <div className="guide-step">
                <span className="step-num">1</span>
                <div className="step-content">
                  <strong>Solapi 회원가입</strong>
                  <p>solapi.com 에서 회원가입 후 API Key / Secret 발급</p>
                </div>
              </div>
              <div className="guide-step">
                <span className="step-num">2</span>
                <div className="step-content">
                  <strong>카카오 비즈니스 채널 생성</strong>
                  <p>카카오톡 채널 관리자센터에서 비즈니스 채널 생성 후 Solapi에 연동</p>
                </div>
              </div>
              <div className="guide-step">
                <span className="step-num">3</span>
                <div className="step-content">
                  <strong>알림톡 템플릿 등록</strong>
                  <p>Solapi 대시보드에서 메시지 템플릿을 등록하고 카카오 심사 통과</p>
                </div>
              </div>
              <div className="guide-step">
                <span className="step-num">4</span>
                <div className="step-content">
                  <strong>application.properties 수정</strong>
                  <p>아래 설정값을 입력하면 바로 실서비스 전환됩니다.</p>
                  <div className="code-block">
                    <code>
                      notification.sender=solapi<br/>
                      solapi.api-key=발급받은_API_KEY<br/>
                      solapi.api-secret=발급받은_API_SECRET<br/>
                      solapi.sender-phone=02-1234-5678<br/>
                      solapi.kakao-pfid=카카오_채널_ID
                    </code>
                  </div>
                </div>
              </div>
            </div>

            <div className="guide-info-box">
              <h4>📌 자동 발송 시점</h4>
              <ul>
                <li><strong>예약 접수 시</strong> — 모든 고객에게 예약 확정 대기 안내</li>
                <li><strong>예약 확정 시</strong> — 관리자 확인 후 확정 알림</li>
                <li><strong>매일 오전 10시</strong> — 내일 예약 고객에게 D-1 리마인드</li>
                <li><strong>시술 완료 시</strong> — 감사 메시지 + 주의사항 안내</li>
                <li><strong>예약 취소 시</strong> — 취소 안내 + 재예약 유도</li>
              </ul>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationPage;
